import React, { useState, useEffect } from 'react';
import DashboardLayout from "../layouts/DashboardLayout";
import InfoModal from "../layouts/InfoModal";
import PaginatedTableContainer from "../containers/PaginatedTableContainer";
import {
    getAllMembers,
    searchMembers,
    getMember,
    getMemberImageUrl,
    resetMemberPassword,
    changeMemberStatus,
    changeMemberPoint,
    hardDeleteMember
} from "../../api/AdminApi";

const MemberManagementPage = () => {
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);

    // 검색 관련 상태
    const [searchQuery, setSearchQuery] = useState('');
    const [searchField, setSearchField] = useState('id');
    const [isSearchMode, setIsSearchMode] = useState(false);

    // 모달 관련 상태
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedMember, setSelectedMember] = useState(null);
    const [modalLoading, setModalLoading] = useState(false);

    // 탭 관련 상태
    const [activeTab, setActiveTab] = useState('info');

    // 이미지 관련 상태
    const [memberImageUrl, setMemberImageUrl] = useState(null);
    const [imageLoading, setImageLoading] = useState(false);

    // 비밀번호 재설정
    const [newPassword, setNewPassword] = useState('');

    // 상태 변경
    const [selectedStatus, setSelectedStatus] = useState('');

    // 점수 수정
    const [newPoint, setNewPoint] = useState('');

    const statusOptions = [
        { value: 'CREATED', label: 'CREATED' },
        { value: 'ACTIVE', label: 'ACTIVE' },
        { value: 'BANNED', label: 'BANNED' },
        { value: 'WITHDRAWN', label: 'WITHDRAWN' },
        { value: 'PHONE_V_REQUIRED', label: 'PHONE_V_REQUIRED' }
    ];

    const fetchMembers = async (page = 0) => {
        setLoading(true);
        try {
            const response = await getAllMembers(page, 20);
            setMembers(response.content);
            setCurrentPage(response.currentPage);
            setTotalPages(response.totalPages);
            setTotalElements(response.totalElements);
            setIsSearchMode(false);
        } catch (error) {
            console.error('멤버 목록 조회 실패:', error);
            alert('멤버 목록을 불러오는데 실패했습니다.');
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = async (page = 0) => {
        if (!searchQuery.trim()) {
            alert('검색어를 입력해주세요.');
            return;
        }

        setLoading(true);
        try {
            const response = await searchMembers(page, 20, searchQuery, searchField);
            setMembers(response.content);
            setCurrentPage(response.currentPage);
            setTotalPages(response.totalPages);
            setTotalElements(response.totalElements);
            setIsSearchMode(true);
        } catch (error) {
            console.error('멤버 검색 실패:', error);
            alert('멤버 검색에 실패했습니다.');
        } finally {
            setLoading(false);
        }
    };

    const handleRowClick = async (member) => {
        setModalLoading(true);
        setModalOpen(true);
        setActiveTab('info'); // 기본 탭으로 설정
        try {
            const memberDetail = await getMember(member.id);
            setSelectedMember(memberDetail);
            setSelectedStatus(memberDetail.status);
            setNewPassword('');
            setNewPoint(memberDetail.point?.toString() || '0');

            // 이미지 URL 초기화
            setMemberImageUrl(null);
        } catch (error) {
            console.error('멤버 상세 조회 실패:', error);
            alert('멤버 정보를 불러오는데 실패했습니다.');
            setModalOpen(false);
        } finally {
            setModalLoading(false);
        }
    };

    const handleTabChange = (tab) => {
        setActiveTab(tab);
        if (tab === 'image') {
            // 이미지 탭으로 이동할 때 이미지 URL 초기화
            setMemberImageUrl(null);
        }
    };

    const handleLoadImage = async () => {
        if (!selectedMember || !selectedMember.imageId) {
            alert('이미지 ID가 없습니다.');
            return;
        }

        setImageLoading(true);
        try {
            const response = await getMemberImageUrl(selectedMember.id);
            setMemberImageUrl(response.imageUrl);
        } catch (error) {
            console.error('이미지 URL 조회 실패:', error);
            alert('이미지를 불러오는데 실패했습니다.');
        } finally {
            setImageLoading(false);
        }
    };

    const handlePasswordReset = async () => {
        if (!newPassword.trim()) {
            alert('새 비밀번호를 입력해주세요.');
            return;
        }

        try {
            await resetMemberPassword(selectedMember.id, newPassword);
            alert('비밀번호가 재설정되었습니다.');
            setNewPassword('');
        } catch (error) {
            console.error('비밀번호 재설정 실패:', error);
            alert('비밀번호 재설정에 실패했습니다.');
        }
    };

    const handleStatusChange = async () => {
        try {
            await changeMemberStatus(selectedMember.id, selectedStatus);
            alert('멤버 상태가 변경되었습니다.');
            // 선택된 멤버 정보 업데이트
            setSelectedMember({...selectedMember, status: selectedStatus});
            // 목록 새로고침
            if (isSearchMode && searchQuery.trim()) {
                handleSearch(currentPage);
            } else {
                fetchMembers(currentPage);
            }
        } catch (error) {
            console.error('멤버 상태 변경 실패:', error);
            alert('멤버 상태 변경에 실패했습니다.');
        }
    };

    const handlePointChange = async () => {
        const pointNum = parseInt(newPoint);
        if (isNaN(pointNum) || pointNum < 0 || pointNum > 100) {
            alert('점수는 0부터 100 사이의 숫자여야 합니다.');
            return;
        }

        try {
            await changeMemberPoint(selectedMember.id, pointNum);
            alert('점수가 변경되었습니다.');
            // 선택된 멤버 정보 업데이트
            setSelectedMember({...selectedMember, point: pointNum});
            // 목록 새로고침
            if (isSearchMode && searchQuery.trim()) {
                handleSearch(currentPage);
            } else {
                fetchMembers(currentPage);
            }
        } catch (error) {
            console.error('점수 변경 실패:', error);
            alert('점수 변경에 실패했습니다.');
        }
    };

    const handleHardDelete = async () => {
        const confirmed = window.confirm(`정말로 ${selectedMember.name} (ID: ${selectedMember.id}) 멤버를 데이터베이스에서 영구히 삭제하시겠습니까? 이 동작은 되돌릴 수 없습니다.`);
        if (!confirmed) return;

        try {
            await hardDeleteMember(selectedMember.id);
            alert('멤버가 삭제되었습니다.');
            setModalOpen(false);
            // 목록 새로고침
            if (isSearchMode && searchQuery.trim()) {
                handleSearch(currentPage);
            } else {
                fetchMembers(currentPage);
            }
        } catch (error) {
            console.error('멤버 삭제 실패:', error);
            alert('멤버 삭제에 실패했습니다.');
        }
    };

    useEffect(() => {
        fetchMembers();
    }, []);

    const handlePageChange = (page) => {
        if (page >= 0 && page < totalPages) {
            setCurrentPage(page);
            if (isSearchMode && searchQuery.trim()) {
                handleSearch(page);
            } else {
                fetchMembers(page);
            }
        }
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return '';
        return new Date(dateString).toLocaleString('ko-KR');
    };

    const handleSearchSubmit = (e) => {
        e.preventDefault();
        setCurrentPage(0);
        handleSearch(0);
    };

    const handleReset = () => {
        setSearchQuery('');
        setCurrentPage(0);
        fetchMembers(0);
    };

    // 테이블 컬럼 정의
    const columns = [
        { header: 'id', field: 'id', minWidth: 'min-w-12' },
        { header: 'name', field: 'name', minWidth: 'min-w-16' },
        { header: 'phone', field: 'phone', minWidth: 'min-w-24' },
        {
            header: 'password',
            field: 'password',
            minWidth: 'min-w-16',
            render: (member) => member.password || ''
        },
        {
            header: 'imageId',
            field: 'imageId',
            minWidth: 'min-w-16',
            render: (member) => member.imageId || ''
        },
        {
            header: 'provider',
            field: 'provider',
            minWidth: 'min-w-16',
            render: (member) => member.provider || ''
        },
        {
            header: 'providerId',
            field: 'providerId',
            minWidth: 'min-w-16',
            render: (member) => member.providerId || ''
        },
        { header: 'role', field: 'role', minWidth: 'min-w-20' },
        { header: 'status', field: 'status', minWidth: 'min-w-20' },
        {
            header: 'createdAt',
            field: 'createdAt',
            minWidth: 'min-w-32',
            render: (member) => formatDateTime(member.createdAt)
        },
        {
            header: 'updatedAt',
            field: 'updatedAt',
            minWidth: 'min-w-32',
            render: (member) => formatDateTime(member.updatedAt)
        },
        {
            header: 'nameUpdatedAt',
            field: 'nameUpdatedAt',
            minWidth: 'min-w-32',
            render: (member) => formatDateTime(member.nameUpdatedAt)
        },
        {
            header: 'point',
            field: 'point',
            minWidth: 'min-w-12',
            render: (member) => member.point || 0
        },
        {
            header: 'latitude',
            field: 'latitude',
            minWidth: 'min-w-16',
            render: (member) => member.latitude || ''
        },
        {
            header: 'longitude',
            field: 'longitude',
            minWidth: 'min-w-16',
            render: (member) => member.longitude || ''
        },
        {
            header: 'targetToken',
            field: 'targetToken',
            minWidth: 'min-w-20',
            render: (member) => member.targetToken || ''
        },
        {
            header: 'riceBalance',
            field: 'riceBalance',
            minWidth: 'min-w-16',
            render: (member) => member.riceBalance || 0
        }
    ];

    return (
        <DashboardLayout>
            <div className="p-3">
                <h1 className="text-lg font-bold mb-3">멤버 관리</h1>

                {/* 검색 폼 */}
                <div className="mb-3 p-2 bg-gray-100 border border-gray-300">
                    <form onSubmit={handleSearchSubmit} className="flex items-center gap-2 text-xs">
                        <div className="flex gap-2">
                            <label className="flex items-center">
                                <input
                                    type="radio"
                                    name="searchField"
                                    value="id"
                                    checked={searchField === 'id'}
                                    onChange={(e) => setSearchField(e.target.value)}
                                    className="mr-1"
                                />
                                ID
                            </label>
                            <label className="flex items-center">
                                <input
                                    type="radio"
                                    name="searchField"
                                    value="name"
                                    checked={searchField === 'name'}
                                    onChange={(e) => setSearchField(e.target.value)}
                                    className="mr-1"
                                />
                                이름
                            </label>
                            <label className="flex items-center">
                                <input
                                    type="radio"
                                    name="searchField"
                                    value="phone"
                                    checked={searchField === 'phone'}
                                    onChange={(e) => setSearchField(e.target.value)}
                                    className="mr-1"
                                />
                                전화번호
                            </label>
                        </div>
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="검색어 입력"
                            className="px-2 py-1 border border-gray-300 text-xs"
                        />
                        <button type="submit" className="px-2 py-1 bg-gray-200 border border-gray-300 hover:bg-gray-300 text-xs">
                            검색
                        </button>
                        <button type="button" onClick={handleReset} className="px-2 py-1 bg-gray-200 border border-gray-300 hover:bg-gray-300 text-xs">
                            전체보기
                        </button>
                    </form>
                </div>

                {/* 상단 정보 */}
                <div className="mb-2 text-xs text-gray-600">
                    총 {totalElements}명 | {currentPage + 1}/{totalPages} 페이지
                    {isSearchMode && ` | 검색: ${searchField}="${searchQuery}"`}
                </div>

                {/* 페이지네이션 테이블 */}
                <PaginatedTableContainer
                    columns={columns}
                    data={members}
                    loading={loading}
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                    onRowClick={handleRowClick}
                />

                {/* 멤버 상세 모달 */}
                <InfoModal
                    isOpen={modalOpen}
                    onClose={() => setModalOpen(false)}
                    title={`멤버 정보 - ${selectedMember?.name || ''}`}
                    width="w-[600px]"
                >
                    {modalLoading ? (
                        <div className="text-center py-4">멤버 정보를 불러오는 중...</div>
                    ) : selectedMember ? (
                        <div className="text-xs">
                            {/* 윈도우 2000 스타일 탭 */}
                            <div className="mb-4">
                                <div className="flex border-b border-gray-400">
                                    <button
                                        onClick={() => handleTabChange('info')}
                                        className={`px-3 py-2 text-xs border-l border-t border-r border-gray-400 ${
                                            activeTab === 'info' 
                                                ? 'bg-white border-b-white -mb-px relative z-10' 
                                                : 'bg-gray-200 border-b-gray-400'
                                        }`}
                                    >
                                        멤버 정보
                                    </button>
                                    <button
                                        onClick={() => handleTabChange('edit')}
                                        className={`px-3 py-2 text-xs border-t border-r border-gray-400 ${
                                            activeTab === 'edit' 
                                                ? 'bg-white border-b-white -mb-px relative z-10' 
                                                : 'bg-gray-200 border-b-gray-400'
                                        }`}
                                    >
                                        정보 수정
                                    </button>
                                    <button
                                        onClick={() => handleTabChange('image')}
                                        className={`px-3 py-2 text-xs border-t border-r border-gray-400 ${
                                            activeTab === 'image' 
                                                ? 'bg-white border-b-white -mb-px relative z-10' 
                                                : 'bg-gray-200 border-b-gray-400'
                                        }`}
                                    >
                                        이미지 보기
                                    </button>
                                </div>
                            </div>

                            {/* 탭 내용 */}
                            {activeTab === 'info' ? (
                            <div>
                                <h3 className="font-bold mb-2 text-sm">멤버 정보</h3>
                                <table className="w-full border border-gray-400">
                                    <tbody>
                                        <tr><td className="border border-gray-400 px-2 py-1 bg-gray-100 w-24">id</td><td className="border border-gray-400 px-2 py-1">{selectedMember.id}</td></tr>
                                        <tr><td className="border border-gray-400 px-2 py-1 bg-gray-100">name</td><td className="border border-gray-400 px-2 py-1">{selectedMember.name}</td></tr>
                                        <tr><td className="border border-gray-400 px-2 py-1 bg-gray-100">phone</td><td className="border border-gray-400 px-2 py-1">{selectedMember.phone}</td></tr>
                                        <tr><td className="border border-gray-400 px-2 py-1 bg-gray-100">password</td><td className="border border-gray-400 px-2 py-1">{selectedMember.password || ''}</td></tr>
                                        <tr><td className="border border-gray-400 px-2 py-1 bg-gray-100">imageId</td><td className="border border-gray-400 px-2 py-1">{selectedMember.imageId || ''}</td></tr>
                                        <tr><td className="border border-gray-400 px-2 py-1 bg-gray-100">provider</td><td className="border border-gray-400 px-2 py-1">{selectedMember.provider || ''}</td></tr>
                                        <tr><td className="border border-gray-400 px-2 py-1 bg-gray-100">providerId</td><td className="border border-gray-400 px-2 py-1">{selectedMember.providerId || ''}</td></tr>
                                        <tr><td className="border border-gray-400 px-2 py-1 bg-gray-100">role</td><td className="border border-gray-400 px-2 py-1">{selectedMember.role}</td></tr>
                                        <tr><td className="border border-gray-400 px-2 py-1 bg-gray-100">status</td><td className="border border-gray-400 px-2 py-1">{selectedMember.status}</td></tr>
                                        <tr><td className="border border-gray-400 px-2 py-1 bg-gray-100">createdAt</td><td className="border border-gray-400 px-2 py-1">{formatDateTime(selectedMember.createdAt)}</td></tr>
                                        <tr><td className="border border-gray-400 px-2 py-1 bg-gray-100">updatedAt</td><td className="border border-gray-400 px-2 py-1">{formatDateTime(selectedMember.updatedAt)}</td></tr>
                                        <tr><td className="border border-gray-400 px-2 py-1 bg-gray-100">nameUpdatedAt</td><td className="border border-gray-400 px-2 py-1">{formatDateTime(selectedMember.nameUpdatedAt)}</td></tr>
                                        <tr><td className="border border-gray-400 px-2 py-1 bg-gray-100">point</td><td className="border border-gray-400 px-2 py-1">{selectedMember.point || 0}</td></tr>
                                        <tr><td className="border border-gray-400 px-2 py-1 bg-gray-100">latitude</td><td className="border border-gray-400 px-2 py-1">{selectedMember.latitude || ''}</td></tr>
                                        <tr><td className="border border-gray-400 px-2 py-1 bg-gray-100">longitude</td><td className="border border-gray-400 px-2 py-1">{selectedMember.longitude || ''}</td></tr>
                                        <tr><td className="border border-gray-400 px-2 py-1 bg-gray-100">targetToken</td><td className="border border-gray-400 px-2 py-1">{selectedMember.targetToken || ''}</td></tr>
                                        <tr><td className="border border-gray-400 px-2 py-1 bg-gray-100">riceBalance</td><td className="border border-gray-400 px-2 py-1">{selectedMember.riceBalance || 0}</td></tr>
                                    </tbody>
                                </table>
                            </div>
                            ) : activeTab === 'edit' ? (
                                <div className="space-y-4">
                            {/* 비밀번호 재설정 */}
                            <div>
                                <h3 className="font-bold mb-2 text-sm">비밀번호 재설정 (카카오 유저는 비밀번호 없습니다)</h3>
                                <div className="flex gap-2 items-center">
                                    <input
                                        type="text"
                                        value={newPassword}
                                        onChange={(e) => setNewPassword(e.target.value)}
                                        placeholder="새 비밀번호 입력"
                                        className="px-2 py-1 border border-gray-300 flex-1"
                                        disabled={selectedMember.provider !== null}
                                    />
                                    <button
                                        onClick={handlePasswordReset}
                                        className="px-3 py-1 bg-gray-200 border border-gray-400 hover:bg-gray-300"
                                        disabled={selectedMember.provider !== null}
                                    >
                                        재설정
                                    </button>
                                </div>
                            </div>

                            {/* 상태 변경 */}
                            <div>
                                <h3 className="font-bold mb-2 text-sm">상태 변경</h3>
                                <div className="flex gap-2 items-center">
                                    <select
                                        value={selectedStatus}
                                        onChange={(e) => setSelectedStatus(e.target.value)}
                                        className="px-2 py-1 border border-gray-300 flex-1"
                                    >
                                        {statusOptions.map(option => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </select>
                                    <button
                                        onClick={handleStatusChange}
                                        className="px-3 py-1 bg-gray-200 border border-gray-400 hover:bg-gray-300"
                                    >
                                        적용
                                    </button>
                                </div>
                            </div>

                                    {/* 점수 수정 */}
                                    <div>
                                        <h3 className="font-bold mb-2 text-sm">점수 수정</h3>
                                        <div className="flex gap-2 items-center">
                                            <input
                                                type="number"
                                                min="0"
                                                max="100"
                                                value={newPoint}
                                                onChange={(e) => setNewPoint(e.target.value)}
                                                placeholder="0~100 사이의 점수 입력"
                                                className="px-2 py-1 border border-gray-300 flex-1"
                                            />
                                            <button
                                                onClick={handlePointChange}
                                                className="px-3 py-1 bg-gray-200 border border-gray-400 hover:bg-gray-300"
                                                disabled={!newPoint.trim() || newPoint === selectedMember.point?.toString()}
                                            >
                                                저장
                                            </button>
                                        </div>
                                        <div className="text-xs text-gray-500 mt-1">
                                            0~100 사이의 숫자를 입력해주세요.
                                        </div>
                                    </div>

                            {/* 하드 삭제 */}
                            <div>
                                <h3 className="font-bold mb-2 text-sm">Hard Delete</h3>
                                <button
                                    onClick={handleHardDelete}
                                    className="px-3 py-1 bg-red-200 border border-gray-400 hover:bg-red-300 text-red-800"
                                >
                                    멤버 영구 삭제
                                </button>
                            </div>
                                </div>
                            ) : (
                                /* 이미지 보기 탭 */
                                <div>
                                    <h3 className="font-bold mb-2 text-sm">멤버 이미지</h3>
                                    <div className="border border-gray-400 p-3">
                                        <div className="mb-2">
                                            <button
                                                onClick={handleLoadImage}
                                                className="px-3 py-1 bg-gray-200 border border-gray-400 hover:bg-gray-300"
                                                disabled={!selectedMember?.imageId || imageLoading}
                                            >
                                                {imageLoading ? '로딩 중...' : '이미지 불러오기'}
                                            </button>
                                            {!selectedMember?.imageId && (
                                                <span className="ml-2 text-xs text-gray-500">이미지 ID가 없습니다.</span>
                                            )}
                                        </div>
                                        {memberImageUrl && (
                                            <div className="mt-2">
                                                <img
                                                    src={memberImageUrl}
                                                    alt="멤버 프로필"
                                                    className="max-w-full max-h-64 border border-gray-300"
                                                    onError={() => {
                                                        alert('이미지를 불러올 수 없습니다.');
                                                        setMemberImageUrl(null);
                                                    }}
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    ) : null}
                </InfoModal>
            </div>
        </DashboardLayout>
    );
};

export default MemberManagementPage;
